import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { twMerge } from 'tailwind-merge';

import { Telemetry } from '@videoblocks/kafka-rest-client';
import { getTrendingTagLinks } from '@videoblocks/shared-components';
import { ProgressiveImage } from '@videoblocks/storywind';

import { selectAuthFeatureFlags } from '../../../auth/AuthSelectors';
import { SearchFilterContentTypes } from '../../Shared/enums';
import { SearchHeroBuilderData } from '../hooks/useSearchHeroData';
import getAssetSource from '../utils/getAssetSource';
import getLandingHeroContentTypes from '../utils/getLandingHeroContentTypes';
import ProminentSearchContainer from './ProminentSearch/ProminentSearchContainer';
import {
  ConversationalSearchProvider,
  useConversationalSearch,
} from './navSearch/NavSearchContext';

const imagesUrl = process.env.NEXT_PUBLIC_ASSET_PATH ?? '/images';

// WCAG 2.2 (2.2.2): auto-playing motion must be avoidable. Safe to read during render
// because the hero data is fetched in an effect, so the video only mounts client-side.
const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);

const eventTrackingForProminentSearch = () => {
  Telemetry.increment('homepagesearch.trending_keyword_click');
};

export interface Props {
  currentContentType: SearchFilterContentTypes;
  builderData: SearchHeroBuilderData | null;
  isLoading: boolean;
}

const heroContainerClass =
  'w-full h-160 lg:h-128 flex flex-col lg:flex-row items-center gap-x-8';

// Full-width placeholder shown while the Builder data is loading
const HeroSkeleton = () => (
  <div className={heroContainerClass}>
    <div className="lg:w-7/12 w-full flex flex-col justify-center gap-y-4 px-6 lg:px-12 py-4 order-2 lg:order-1">
      <div className="h-10 lg:h-12 w-3/4 bg-gray-300 rounded animate-pulse" />
      <div className="h-6 w-1/2 bg-gray-300 rounded animate-pulse" />
      <div className="h-14 w-full bg-gray-300 rounded-full animate-pulse mt-8" />
      <div className="flex gap-x-2 mt-4">
        <div className="h-5 w-20 bg-gray-300 rounded animate-pulse" />
        <div className="h-5 w-24 bg-gray-300 rounded animate-pulse" />
        <div className="h-5 w-16 bg-gray-300 rounded animate-pulse" />
      </div>
    </div>
    <div
      className="lg:w-5/12 w-full h-1/2 lg:h-full bg-gray-300 animate-pulse order-1 lg:order-2 ml-4 lg:ml-0"
      style={{ borderBottomLeftRadius: '4rem' }}
    />
  </div>
);

const LandingSearchHero = (props: Props) => (
  <ConversationalSearchProvider>
    <ProminentSearchContent {...props} />
  </ConversationalSearchProvider>
);

function ProminentSearchContent({
  currentContentType,
  builderData,
  isLoading,
}: Props) {
  const contentTypes = getLandingHeroContentTypes();
  const [contentType, setContentType] = useState(currentContentType);
  const { conversationalSearchEnabled } = useConversationalSearch();
  const { showConversationalSearch } = useSelector(selectAuthFeatureFlags);
  // Read once: autoPlay is only honoured when the element is created.
  const [autoPlayHeroVideo] = useState(() => !prefersReducedMotion());

  if (isLoading) return <HeroSkeleton />;

  if (!builderData) return null;

  return (
    <div className={heroContainerClass}>
      {/*Header Container*/}
      <div className="lg:w-7/12 w-full flex order-2 lg:order-1">
        <div
          className={twMerge(
            'gap-y-16 w-full max-w-screen-2xl grid grid-cols-12 px-6 lg:px-12',
            showConversationalSearch ? 'lg:px-6' : 'lg:px-12'
          )}
        >
          {/*Header*/}
          <div className="justify-center flex flex-col col-span-full py-4 lg:py-0 lg:px-4">
            {/*Header Text*/}
            <div className="justify-center flex flex-col gap-y-4">
              <h1 className="font-polysans font-semibold text-gray-900 mb-0 text-3xl lg:text-4xl mt-0">
                {conversationalSearchEnabled
                  ? 'Find the perfect video asset with AI Search'
                  : builderData.headingFirstLine}
              </h1>
              <h2 className="font-polysans font-semibold text-gray-700 mb-0 text-lg mt-0">
                {builderData.headingSecondLine}
              </h2>
            </div>
            {/*Search Bar*/}
            <>
              <div className="prominent-search-bar pt-8">
                <ProminentSearchContainer
                  imagesUrl={imagesUrl}
                  contentTypes={contentTypes}
                  contentType={contentType}
                  setContentType={setContentType}
                  showDropdown={false}
                  showContentToggles={true}
                  isLanding={true}
                />
              </div>
              {!conversationalSearchEnabled &&
                builderData.trendingTags.length > 0 && (
                  <div className="flex mt-4 flex-wrap">
                    <span className="font-inter text-sm font-semibold">
                      Trending searches:&nbsp;
                    </span>
                    {builderData.trendingTags.map((tag, idx) => (
                      <a
                        className="font-inter text-gray-800 text-sm font-semibold no-underline"
                        href={getTrendingTagLinks(
                          tag.searchTerm.toLowerCase().replace(/\s/g, '-'),
                          contentType
                        )}
                        onClick={eventTrackingForProminentSearch}
                        key={tag.searchTerm}
                      >
                        <span
                          style={{
                            textDecoration: 'underline',
                            textDecorationStyle: 'dotted',
                            textUnderlineOffset: '0.25rem',
                          }}
                        >
                          {tag.searchTerm}
                        </span>
                        {idx === builderData.trendingTags.length - 1 ? '' : ','}
                        &nbsp;
                      </a>
                    ))}
                  </div>
                )}
            </>
          </div>
        </div>
      </div>
      {/*Background*/}
      <div
        className="lg:w-5/12 lg:h-full h-1/2 overflow-hidden order-1 lg:order-2 ml-4 lg:ml-0"
        style={{ borderBottomLeftRadius: '4rem' }}
      >
        <div className="h-full whitespace-nowrap transition-all">
          {builderData.videoSrc ? (
            <video
              className="w-screen h-full object-cover"
              poster={getAssetSource(builderData.imageSrc)}
              loop
              autoPlay={autoPlayHeroVideo}
              muted
              playsInline
            >
              <source src={getAssetSource(builderData.videoSrc)} />
            </video>
          ) : (
            <ProgressiveImage
              className="w-screen h-full object-cover"
              src={getAssetSource(builderData.imageSrc)}
              alt="Background Image"
              shouldLazyLoad={true}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default LandingSearchHero;
